/* Product-to-product navigation for phones and tablets. Normal page navigation
   keeps Shopify page views, variant state and browser history in sync. */
(() => {
  if (window.giestoProductSwipe) return;
  window.giestoProductSwipe = true;
  const narrow = matchMedia('(max-width:1024px)');
  const key = 'giesto:product-browse';
  const age = 30 * 60 * 1000;
  const pathOf = raw => {
    try {
      const url = new URL(raw, location.origin);
      if (url.origin === location.origin && /\/products\/[^/]+\/?$/.test(url.pathname)) return url.pathname;
    } catch {}
    return '';
  };
  const identity = raw => pathOf(raw).replace(/\/collections\/[^/]+(?=\/products\/)/, '').replace(/\/$/, '');
  const isCurrent = item => identity(item.url) === identity(location.pathname);
  const remember = value => { try { sessionStorage.setItem(key, JSON.stringify(value)); } catch {} };
  const read = () => {
    try {
      const value = JSON.parse(sessionStorage.getItem(key));
      if (Date.now() - value?.time < age && Array.isArray(value?.items)) {
        const items = value.items.filter(item => pathOf(item.url));
        if (items.some(isCurrent)) return {...value, items};
      }
    } catch {}
    return null;
  };
  function mount(root) {
    if (root.productSwipe) return;
    root.productSwipe = true;
    let list = null, loading = null, gesture = null, leaving = false, suppressUntil = 0;
    let wheelDistance = 0, wheelTime = 0, wheelTimer = 0;
    let motionTimer = 0, fixed = [], stage = null, disposePreview = null;
    const previews = new Map();
    const removeStage = () => { disposePreview?.(); disposePreview = null; stage?.remove(); stage = null; };
    const reset = () => {
      gesture = null; wheelDistance = 0; clearTimeout(wheelTimer); clearTimeout(motionTimer);
      root.classList.remove('gps-dragging','gps-moving','gps-settling'); root.style.removeProperty('--gps-offset');
      for (const element of fixed) {
        element.removeAttribute('data-gps-fixed');
        for (const name of ['x','y','width']) element.style.removeProperty(`--gps-fixed-${name}`);
      }
      fixed = []; removeStage(); document.documentElement.classList.remove('gps-active');
    };
    const sync = () => {
      leaving = false;
      reset();
      root.classList.toggle('gps-enabled', narrow.matches && Boolean(list?.items.length > 1));
      if (narrow.matches) void load();
      if (narrow.matches && list) for (const dx of [-1,1]) { const item = neighbour(dx); if (item) void previewHTML(item); }
    };
    async function load() {
      if (list || loading || !narrow.matches) return loading;
      list = read();
      if (list) { sync(); return; }
      loading = (async () => {
        try {
          const url = new URL(root.dataset.swipeCollection, location.origin);
          if (url.origin !== location.origin || !/\/collections\//.test(url.pathname)) return;
          url.searchParams.set('view', 'giesto-story-index');
          const preview = new URL(location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          const items = [];
          // The existing lightweight collection index paginates at 250 products.
          for (let page = 1; page <= 8; page++) {
            if (!root.isConnected || !narrow.matches) return;
            url.searchParams.set('page', page);
            const response = await fetch(url, {credentials:'same-origin'});
            if (!response.ok) return;
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            const data = JSON.parse(doc.querySelector('[data-story-index]')?.textContent || 'null');
            if (!Array.isArray(data?.products)) return;
            for (const product of data.products) {
              const path = pathOf(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(product[8])}`);
              if (path && !items.some(item => item.url === path)) items.push({url:path, title:product[1]});
            }
            if (page >= data.pages) break;
          }
          if (!items.some(isCurrent)) return;
          list = {time:Date.now(), items};
          remember(list);
          sync();
        } catch { /* A failed catalogue read leaves the existing product usable. */ }
      })().finally(() => { loading = null; });
      return loading;
    }
    const blocked = target => [...document.querySelectorAll('dialog[open], [aria-modal="true"]')].some(element => {
      const box = element.getBoundingClientRect();
      return box.width > 0 && box.height > 0 && box.bottom > 0 && box.top < innerHeight && box.right > 0 && box.left < innerWidth &&
        !element.closest('[hidden], [inert], [aria-hidden="true"]') && getComputedStyle(element).visibility !== 'hidden';
    }) ||
      target.closest('a,input,select,textarea,video,[role="tablist"],.gmp-tabs,.gar,.gap__thumbs,[data-zp-pairings],.gaa__zoom,.ga-product-zoom') ||
      (target.closest('button') && !target.closest('[data-gap-zoom]'));
    const neighbour = dx => {
      const index = list?.items.findIndex(isCurrent) ?? -1;
      return index < 0 ? null : list.items[index + (dx < 0 ? 1 : -1)];
    };
    const productURL = item => item.url + (new URL(location.href).searchParams.has('preview_theme_id') ?
      `?preview_theme_id=${encodeURIComponent(new URL(location.href).searchParams.get('preview_theme_id'))}` : '');
    function previewHTML(item) {
      if (!previews.has(item.url)) previews.set(item.url, fetch(productURL(item), {credentials:'same-origin'})
        .then(response => response.ok ? response.text() : null).catch(() => null));
      return previews.get(item.url);
    }
    function showStage(dx) {
      const item = neighbour(dx);
      if (!item) { removeStage(); return; }
      if (stage?.dataset.productUrl === item.url) return;
      removeStage();
      const panel = document.createElement('div');
      panel.className = 'gps-neighbour'; panel.dataset.productUrl = item.url;
      panel.inert = true; panel.setAttribute('aria-hidden','true');
      document.body.append(panel); stage = panel;
      const scroll = scrollY + (matchMedia('(min-width:990px)').matches ? document.querySelector('.page-wrapper')?.scrollTop || 0 : 0);
      const top = root.getBoundingClientRect().top + scroll;
      void previewHTML(item).then(html => {
        if (!html || stage !== panel) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        let copy = doc.querySelector('giesto-store-product');
        if (!copy) return;
        // The preview is inert markup, never a second running storefront.
        // Strip executable content and custom elements before adopting it.
        for (const element of copy.querySelectorAll('script:not([type="application/json"]),iframe,object,embed')) element.remove();
        for (let element of [copy,...copy.querySelectorAll('*')].reverse()) {
          for (const attribute of [...element.attributes]) if (/^on/i.test(attribute.name)) element.removeAttribute(attribute.name);
          if (element.tagName.includes('-')) {
            const replacement = doc.createElement('div');
            for (const attribute of [...element.attributes]) replacement.setAttribute(attribute.name, attribute.value);
            replacement.append(...element.childNodes); element.replaceWith(replacement);
            if (element === copy) copy = replacement;
            element = replacement;
          }
          if (element.id) element.id = `gps-preview-${element.id}`;
          if (element.tagName === 'VIDEO' || element.tagName === 'AUDIO') {
            element.removeAttribute('autoplay'); element.removeAttribute('src'); element.setAttribute('preload','none');
            element.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
          }
        }
        const adopted = document.importNode(copy,true);
        adopted.classList.add('gps-preview'); adopted.style.top = `${top}px`;
        panel.append(adopted);
        const prepare = () => { if (stage === panel) disposePreview = window.GiestoMobileProductPreview?.(adopted) || null; };
        if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',prepare,{once:true}); else prepare();
      }).catch(() => { /* Native navigation remains available if a preview cannot render. */ });
    }
    const move = dx => {
      if (!root.classList.contains('gps-moving')) {
        const box = root.getBoundingClientRect();
        for (const element of root.querySelectorAll('*')) if (getComputedStyle(element).position === 'fixed') {
          const bounds = element.getBoundingClientRect();
          if (!bounds.width || !bounds.height) continue;
          element.style.setProperty('--gps-fixed-x',`${bounds.left-box.left}px`);
          element.style.setProperty('--gps-fixed-y',`${bounds.top-box.top}px`);
          element.style.setProperty('--gps-fixed-width',`${bounds.width}px`);
          element.setAttribute('data-gps-fixed',''); fixed.push(element);
        }
        root.classList.add('gps-moving'); document.documentElement.classList.add('gps-active');
      }
      showStage(dx);
      root.classList.add('gps-dragging');
      const offset = neighbour(dx) ? dx : dx * .15;
      root.style.setProperty('--gps-offset',`${Math.max(-innerWidth,Math.min(innerWidth,offset))}px`);
      if (stage) stage.style.translate = `${(dx < 0 ? innerWidth : -innerWidth) + offset}px 0`;
    };
    const settle = (dx, done) => {
      root.getBoundingClientRect(); // Start from the last painted drag offset.
      root.classList.remove('gps-dragging'); root.classList.add('gps-settling');
      if (stage) stage.classList.add('gps-settling');
      root.style.setProperty('--gps-offset',`${dx}px`);
      if (stage) stage.style.translate = `${dx ? 0 : (parseFloat(stage.style.translate) >= 0 ? innerWidth : -innerWidth)}px 0`;
      motionTimer = setTimeout(done, matchMedia('(prefers-reduced-motion:reduce)').matches ? 0 : 250);
    };
    const navigate = dx => {
      const destination = neighbour(dx);
      if (!root.isConnected || !narrow.matches) { leaving = false; reset(); return; }
      if (leaving) return;
      if (Math.abs(dx) < innerWidth / 6 || !destination) { settle(0,reset); return; }
      leaving = true;
      remember({...list,time:Date.now()});
      // Reuse the existing consent-aware analytics bridge and its registered fields.
      document.dispatchEvent(new CustomEvent('giesto:funnel', {detail:{name:'select_item', fields:{
        list:'urun_kaydirma', product_handle:decodeURIComponent(destination.url.split('/products/')[1]),
        product_title:destination.title || '', position:list.items.indexOf(destination)+1
      }}}));
      // Keep both panels moving through release. The incoming inert panel
      // remains centred until its real document takes over; never reset to 0.
      settle(dx < 0 ? -innerWidth : innerWidth, () => location.assign(productURL(destination)));
    };
    root.addEventListener('touchstart', event => {
      if (leaving) return;
      reset();
      if (!narrow.matches || leaving || !list || event.touches.length !== 1 || blocked(event.target)) return;
      const touch = event.touches[0];
      // Preserve the native back/forward gesture at either screen edge.
      if (touch.clientX < innerWidth * .1 || touch.clientX > innerWidth * .9) return;
      gesture = {x:touch.clientX, y:touch.clientY, dx:0, horizontal:false};
    }, {passive:true});
    root.addEventListener('touchmove', event => {
      if (!gesture) return;
      if (event.touches.length !== 1 || !narrow.matches || blocked(event.target)) { reset(); return; }
      const dx = event.touches[0].clientX - gesture.x;
      const dy = event.touches[0].clientY - gesture.y;
      if (!gesture.horizontal) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 10) return;
        if (Math.abs(dx) <= Math.abs(dy) * 1.5) { reset(); return; }
        gesture.horizontal = true;
      }
      if (!event.cancelable) { reset(); return; }
      event.preventDefault();
      gesture.dx = dx;
      suppressUntil = Date.now() + 500;
      move(dx);
    }, {passive:false});
    root.addEventListener('touchend', event => {
      if (!gesture) return;
      const {dx,horizontal} = gesture;
      if (horizontal && event.cancelable) event.preventDefault();
      gesture = null;
      if (!event.touches.length && horizontal) navigate(dx); else reset();
    }, {passive:false});
    root.addEventListener('touchcancel', reset, {passive:true});
    // Desktop responsive previews send trackpad swipes as wheel input, rather
    // than touch events. Settle the burst before navigating so its momentum
    // cannot continue through several products after the document changes.
    root.addEventListener('wheel', event => {
      if (gesture || leaving) return;
      if (!narrow.matches || !list || event.ctrlKey || blocked(event.target)) { reset(); return; }
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? innerWidth : 1;
      const dx = event.deltaX * unit, dy = event.deltaY * unit;
      if (!dx || Math.abs(dx) <= Math.abs(dy) * 1.5 || !event.cancelable) { reset(); return; }
      event.preventDefault();
      const now = performance.now();
      if (now - wheelTime > 220 || Math.sign(wheelDistance) !== Math.sign(dx)) wheelDistance = 0;
      wheelTime = now;
      wheelDistance += dx;
      move(-wheelDistance);
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(() => {
        const distance = -wheelDistance;
        wheelDistance = 0;
        if (!blocked(event.target)) navigate(distance); else reset();
      }, 180);
    }, {passive:false});
    root.addEventListener('click', event => {
      if (Date.now() < suppressUntil) { event.preventDefault(); event.stopImmediatePropagation(); }
    }, true);
    narrow.addEventListener('change', sync);
    document.addEventListener('shopify:section:unload', event => { if (event.target.contains(root)) { leaving = false; reset(); } });
    addEventListener('pageshow', event => { if (event.persisted) { leaving = false; reset(); } });
    sync();
  }
  const init = () => document.querySelectorAll('giesto-store-product[data-swipe-collection]').forEach(mount);
  init();
  document.addEventListener('shopify:section:load', init);
})();
